import { useWallet } from "@solana/wallet-adapter-react";
import { useAccount } from "wagmi";
import { useAuth } from "./useAuth";
import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import tokenService from "services/token.service";
import { getLoggingService } from "../services/logging.service";
import { LogAction } from "../types/logging.types";

interface SolanaWalletAssociationState {
  isAssociated: boolean;
  isLoading: boolean;
  isCreating: boolean;
  error: string | null;
  checkAssociation: () => Promise<boolean>;
  checkAssociationAndAutoAssociate: () => Promise<void>;
}

interface UseSolanaWalletAssociationProps {
  requiresSolana?: boolean;
}

export const useSolanaWalletAssociation = ({ requiresSolana = false }: UseSolanaWalletAssociationProps = {}): SolanaWalletAssociationState => {
  const { publicKey, connected } = useWallet();
  const { address: evmAddress } = useAccount();
  const isAuth = useAuth();
  const [isAssociated, setIsAssociated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastCallRef = useRef<string>("");
  const isCheckingRef = useRef(false);
  const logger = getLoggingService();

  const checkAssociation = useCallback(async () => {
    if (!publicKey || !evmAddress || !isAuth) {
      setIsAssociated(false);
      return false;
    }

    // Create a unique key for this call to prevent duplicate requests
    const callKey = `${evmAddress}-${publicKey.toString()}`;
    if (lastCallRef.current === callKey && isCheckingRef.current) {
      return isAssociated; // Return current state to prevent duplicate calls
    }

    // If we already have the result for this combination and we're associated, return true
    if (lastCallRef.current === callKey && isAssociated) {
      return true;
    }

    lastCallRef.current = callKey;
    isCheckingRef.current = true;
    setIsLoading(true);
    setError(null);

    try {
      const token = tokenService.tokens?.accessToken;
      const response = await axios.get(
        `${process.env.REACT_APP_API_SECONDARY}/wallet/association/${evmAddress}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const isAssociatedResult = response.data.isAssociated || false;
      console.log("Association check result:", isAssociatedResult);

      // Log successful association check
      logger.logWalletConnection('solana', LogAction.ASSOCIATION_CHECK, {
        evmAddress,
        solanaAddress: publicKey.toString(),
        isAssociated: isAssociatedResult
      });

      setIsAssociated(isAssociatedResult);
      return isAssociatedResult;
    } catch (error: any) {
      console.error("Error checking wallet association:", error);

      // Log association check error
      logger.logConnectionError('solana', error instanceof Error ? error : new Error('Association check failed'), {
        evmAddress,
        solanaAddress: publicKey?.toString(),
        statusCode: error.response?.status,
        phase: 'association_check'
      });

      if (error.response?.status === 404) {
        // API endpoint not found - this should show an error
        setIsAssociated(false);
        setError(
          "Wallet association service is not available. Please try again later."
        );
      } else {
        setError(
          error.response?.data?.message || "Failed to check wallet association"
        );
        setIsAssociated(false);
      }
      return false;
    } finally {
      isCheckingRef.current = false;
      setIsLoading(false);
    }
  }, [publicKey, evmAddress, isAuth, isAssociated]);

  const checkAssociationAndAutoAssociate = useCallback(async () => {
    if (!publicKey || !evmAddress || !isAuth) {
      setIsAssociated(false);
      return;
    }

    // First check if association exists
    const isCurrentlyAssociated = await checkAssociation();

    // If not associated, try to create association
    if (!isCurrentlyAssociated) {
      console.log(
        "Wallets not associated, attempting to create association..."
      );
      setIsCreating(true);
      setError(null);

      try {
        const token = tokenService.tokens?.accessToken;
        const response = await axios.post(
          `${process.env.REACT_APP_API_SECONDARY}/wallet/association`,
          {
            evmAddress,
            solanaAddress: publicKey.toString(),
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (response.status === 201) {
          console.log("Wallet association created successfully");

          // Log successful association creation
          logger.logConnectionSuccess('solana', publicKey.toString(), {
            action: 'association_creation',
            evmAddress,
            solanaAddress: publicKey.toString(),
            associationCreated: true
          });

          setIsAssociated(true);
          setError(null);
        }
      } catch (error: any) {
        console.error("Error creating wallet association:", error);

        // Log association creation error
        logger.logConnectionError('solana', error instanceof Error ? error : new Error('Association creation failed'), {
          evmAddress,
          solanaAddress: publicKey.toString(),
          statusCode: error.response?.status,
          phase: 'association_creation',
          errorData: error.response?.data
        });

        if (error.response?.status === 404) {
          // Association API endpoint not found
          console.log("Association API not available");
          setError(
            "Association service is not available. Please try again later."
          );
        } else if (error.response?.status === 409) {
          // Association already exists - treat this as success
          setIsAssociated(true);
          setError(null);
        } else if (error.response?.status === 400) {
          setError("Invalid wallet address format");
        } else {
          setError(
            error.response?.data?.message ||
              "Failed to create wallet association"
          );
        }
      } finally {
        setIsCreating(false);
      }
    }
  }, [publicKey, evmAddress, isAuth, checkAssociation]);

  useEffect(() => {
    // Only run if Solana is required for this IDO
    if (!requiresSolana) {
      setIsAssociated(false);
      setError(null);
      return;
    }

    if (connected && publicKey && evmAddress && isAuth) {
      // Use checkAssociationAndAutoAssociate instead of just checkAssociation
      // This will check association and create it if needed
      void checkAssociationAndAutoAssociate();
    } else {
      setIsAssociated(false);
      setError(null);
      lastCallRef.current = ""; // Reset on disconnect
    }
  }, [
    requiresSolana,
    connected,
    publicKey,
    evmAddress,
    isAuth,
    checkAssociationAndAutoAssociate,
  ]);

  return {
    isAssociated,
    isLoading,
    isCreating,
    error,
    checkAssociation,
    checkAssociationAndAutoAssociate,
  };
};
